import React from "react";
import { Link } from "react-router-dom";
import { PlayCircle, Calendar } from "lucide-react";
import { track, EVENT } from "../../lib/publicAnalytics";

/**
 * VideoCard — embed-ready video tile.
 *
 * BEHAVIOUR (V2.0.0 customer-trust polish):
 *   • If (provider AND src) → real playable demo. Renders the play thumbnail
 *     and the "Watch Demo" action exactly as before.
 *   • If (!provider OR !src) → NO playable demo exists yet. Renders an honest
 *     placeholder + single CTA "Request Personalized Walkthrough" -> /strategy-call.
 *     No "Watch Demo" affordance. No misleading "Video coming soon" implication.
 *
 * Props: slug, title, description, provider, src, category, watch_minutes
 */
export default function VideoCard({ slug, title, description, provider, src, className = "" }) {
  const [open, setOpen] = React.useState(false);
  const hasRealVideo = !!(provider && src);

  const onPlay = () => {
    track(EVENT.DEMO_VIDEO_CLICKED, { slug, provider });
    setOpen(true);
  };

  const onRequestWalkthrough = () => {
    track(EVENT.PERSONALIZED_WALKTHROUGH_CLICKED, { slug, source: "demo_library_card" });
  };

  const embedSrc = hasRealVideo
    ? (provider === "youtube"
        ? `https://www.youtube.com/embed/${src}?autoplay=1`
        : provider === "vimeo"
          ? `https://player.vimeo.com/video/${src}?autoplay=1`
          : src)
    : null;

  return (
    <div className={`border border-[var(--border-soft)] bg-[var(--bg-secondary)] flex flex-col ${className}`} data-testid={`video-card-${slug}`}>
      {hasRealVideo ? (
        <div className="aspect-video bg-slate-900 relative flex items-center justify-center group cursor-pointer" onClick={onPlay} data-testid={`video-play-${slug}`}>
          {!open && (
            <PlayCircle className="w-14 h-14 text-sky-400 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          )}
          {open && (provider === "youtube" || provider === "vimeo") && (
            <iframe title={title} src={embedSrc} className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
          )}
          {open && provider === "self" && (
            <video src={embedSrc} controls autoPlay className="absolute inset-0 w-full h-full" />
          )}
        </div>
      ) : (
        <div className="aspect-video bg-slate-900 relative flex flex-col items-center justify-center text-center px-4" data-testid={`video-placeholder-${slug}`}>
          <Calendar className="w-8 h-8 text-sky-400 mb-2" strokeWidth={1.5} />
          <div className="text-slate-300 text-xs leading-relaxed max-w-[240px]">
            Schedule a strategy call for a personalized walkthrough.
          </div>
          <div className="absolute bottom-2 right-2 font-mono text-[9px] tracking-widest uppercase text-slate-500 border border-slate-700 px-2 py-0.5 bg-slate-950/60">
            Live walkthrough
          </div>
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display font-bold text-slate-100 text-sm leading-snug mb-1.5">{title}</div>
        <div className="text-xs text-slate-400 leading-relaxed flex-1">{description}</div>

        {hasRealVideo ? (
          <button onClick={onPlay} data-testid={`video-watch-${slug}`} className="mt-3 self-start font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5">
            <PlayCircle className="w-3 h-3" strokeWidth={2.5} /> Watch Demo
          </button>
        ) : (
          <Link
            to="/strategy-call"
            onClick={onRequestWalkthrough}
            data-testid={`video-walkthrough-${slug}`}
            className="mt-3 self-start font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300 inline-flex items-center gap-1.5"
          >
            <Calendar className="w-3 h-3" strokeWidth={2.5} /> Request Personalized Walkthrough
          </Link>
        )}
      </div>
    </div>
  );
}
